import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { plainToInstance } from 'class-transformer';
import { Observable } from 'rxjs';
import {
	DefaultErrorMessage,
	mapResponse,
} from 'src/lib/utilities/api/map-response';

import { PagingArgument } from 'src/lib/utilities/api/patterns/pagination/paging.argument';
import {
	ClassroomSearchArgument,
	ClassroomsSearchResultsModel,
} from './models/classroom-search.model';
import {
	ClassroomSessionArgument,
	ClassroomSessionModel,
} from './models/classroom-session.model';
import {
	ClassroomReassignResultModel,
	ClassroomReassignStudentsArgument,
	ClassroomStudentAssignmentModel,
	ClassroomStudentAssignmentResponseModel,
	ClassroomStudentModel,
	ClassroomStudentsTrackerModel,
} from './models/classroom-student.model';
import { ClassroomArgument, ClassroomModel } from './models/classroom.model';

@Injectable({
	providedIn: 'root',
})
export class ClassroomManagerService {
	constructor(private httpClient: HttpClient) {}

	/***************/
	/** CLASSROOM **/
	/***************/

	public searchClassrooms = (
		args: PagingArgument<Partial<ClassroomSearchArgument>>,
	): Observable<ClassroomsSearchResultsModel> => {
		return this.httpClient.post<unknown>(`/api/v1/classrooms`, args).pipe(
			mapResponse((r) => plainToInstance(ClassroomsSearchResultsModel, r), {
				errorCode: '80092058',
			}),
		);
	};

	public getClassroom = (id: number): Observable<ClassroomModel> => {
		return this.httpClient
			.get<{ classroom: unknown }>(`/api/v1/classrooms/${id}`)
			.pipe(
				mapResponse((r) => plainToInstance(ClassroomModel, r.classroom), {
					errorCode: 'E9DF864B',
				}),
			);
	};

	public createClassroom = (
		args: Partial<ClassroomArgument>,
	): Observable<ClassroomModel> => {
		return this.httpClient
			.post<{ classroom: unknown }>(`/api/v1/classrooms/create`, args)
			.pipe(
				mapResponse((r) => plainToInstance(ClassroomModel, r.classroom), {
					errorCode: 'B7DBB134',
					errorMessage: DefaultErrorMessage.Saving,
				}),
			);
	};

	public editClassroom = (
		id: number,
		args: Partial<ClassroomArgument>,
	): Observable<ClassroomModel> => {
		return this.httpClient
			.post<{
				classroom: unknown;
			}>(`/api/v1/classrooms/${id}`, args)
			.pipe(
				mapResponse((r) => plainToInstance(ClassroomModel, r.classroom), {
					errorCode: '64F88847',
					errorMessage: DefaultErrorMessage.Saving,
				}),
			);
	};

	public activateClassroom = (id: number): Observable<ClassroomModel> => {
		return this.httpClient
			.post<{
				classroom: unknown;
			}>(`/api/v1/classrooms/${id}/activate`, {})
			.pipe(
				mapResponse((r) => plainToInstance(ClassroomModel, r.classroom), {
					errorCode: '7CEE6318',
					errorMessage: DefaultErrorMessage.Saving,
				}),
			);
	};

	public archiveClassroom = (id: number): Observable<ClassroomModel> => {
		return this.httpClient
			.post<{
				classroom: unknown;
			}>(`/api/v1/classrooms/${id}/archive`, {})
			.pipe(
				mapResponse((r) => plainToInstance(ClassroomModel, r.classroom), {
					errorCode: 'BDC31321',
					errorMessage: DefaultErrorMessage.Saving,
				}),
			);
	};

	/************************/
	/** CLASSROOM STUDENTS **/
	/************************/

	public getClassroomStudents = (
		classroomId: number,
	): Observable<ClassroomStudentAssignmentModel> => {
		return this.httpClient
			.get<{
				assignments: unknown;
			}>(`/api/v1/classrooms/${classroomId}/students`)
			.pipe(
				mapResponse(
					(r) =>
						plainToInstance(ClassroomStudentAssignmentModel, r.assignments),
					{
						errorCode: '2B2F6A4F',
					},
				),
			);
	};

	public getClassroomStudentsTracker = (
		classroomId: number,
	): Observable<ClassroomStudentsTrackerModel> => {
		return this.httpClient
			.get<unknown>(`/api/v1/classrooms/${classroomId}/students/tracker`)
			.pipe(
				mapResponse((r) => plainToInstance(ClassroomStudentsTrackerModel, r), {
					errorCode: '0CE277FD',
				}),
			);
	};

	public getStudentClassroom = (
		studentId: number,
	): Observable<ClassroomStudentModel> => {
		return this.httpClient
			.get<unknown>(`/api/v1/students/${studentId}/classroom`)
			.pipe(
				mapResponse((r) => plainToInstance(ClassroomStudentModel, r), {
					errorCode: '7855BAF8',
				}),
			);
	};

	public getStudentClassrooms = (
		studentId: number,
	): Observable<ClassroomStudentModel[]> => {
		return this.httpClient
			.get<{ results: unknown[] }>(`/api/v1/students/${studentId}/classrooms`)
			.pipe(
				mapResponse((r) => plainToInstance(ClassroomStudentModel, r.results), {
					errorCode: '6EC5FFAB',
				}),
			);
	};

	public addClassroomStudents = (
		classroomId: number,
		student_ids: number[],
	): Observable<ClassroomStudentAssignmentResponseModel> => {
		return this.httpClient
			.post<{
				assignments: unknown;
			}>(`/api/v1/classrooms/${classroomId}/students/assign`, {
				student_ids,
			})
			.pipe(
				mapResponse(
					(r) => plainToInstance(ClassroomStudentAssignmentResponseModel, r),
					{
						errorCode: 'D67E5038',
						errorMessage: DefaultErrorMessage.Saving,
					},
				),
			);
	};

	public removeClassroomStudent = (
		classroomId: number,
		studentId: number,
	): Observable<unknown> => {
		return this.httpClient
			.post<unknown>(
				`/api/v1/classrooms/${classroomId}/students/${studentId}/remove`,
				{},
			)
			.pipe(
				mapResponse((r) => r, {
					errorCode: 'DD8506FA',
					errorMessage: DefaultErrorMessage.Saving,
				}),
			);
	};

	public reassignClassroomStudents = (
		classroomId: number,
		args: ClassroomReassignStudentsArgument,
	): Observable<ClassroomReassignResultModel> => {
		return this.httpClient
			.post<unknown>(
				`/api/v1/classrooms/${classroomId}/students/reassign`,
				args,
			)
			.pipe(
				mapResponse((r) => plainToInstance(ClassroomReassignResultModel, r), {
					errorCode: '14713FCE',
					errorMessage: DefaultErrorMessage.Saving,
				}),
			);
	};

	/*************/
	/** SESSION **/
	/*************/

	public getSessionList = (): Observable<ClassroomSessionModel[]> => {
		return this.httpClient
			.get<{ results: unknown[] }>(`/api/v1/classroom_sessions`)
			.pipe(
				mapResponse((r) => plainToInstance(ClassroomSessionModel, r.results), {
					errorCode: '0ACBD931',
				}),
			);
	};

	public getSession = (id: number): Observable<ClassroomSessionModel> => {
		return this.httpClient
			.get<{ classroom_session: unknown }>(`/api/v1/classroom_sessions/${id}`)
			.pipe(
				mapResponse(
					(r) => plainToInstance(ClassroomSessionModel, r.classroom_session),
					{
						errorCode: '239FC6D0',
					},
				),
			);
	};

	public createSession = (
		args: ClassroomSessionArgument,
	): Observable<ClassroomSessionModel> => {
		return this.httpClient
			.post<{
				classroom_session: unknown;
			}>(`/api/v1/classroom_sessions/create`, args)
			.pipe(
				mapResponse(
					(r) => plainToInstance(ClassroomSessionModel, r.classroom_session),
					{
						errorCode: '6CC60AEC',
						errorMessage: DefaultErrorMessage.Saving,
					},
				),
			);
	};

	public editSession = (
		id: number,
		args: ClassroomSessionArgument,
	): Observable<ClassroomSessionModel> => {
		return this.httpClient
			.post<{
				classroom_session: unknown;
			}>(`/api/v1/classroom_sessions/${id}`, args)
			.pipe(
				mapResponse(
					(r) => plainToInstance(ClassroomSessionModel, r.classroom_session),
					{
						errorCode: '34B1AA0C',
						errorMessage: DefaultErrorMessage.Saving,
					},
				),
			);
	};

	public activateSession = (id: number): Observable<ClassroomSessionModel> => {
		return this.httpClient
			.post<{
				classroom_session: unknown;
			}>(`/api/v1/classroom_sessions/${id}/activate`, {})
			.pipe(
				mapResponse(
					(r) => plainToInstance(ClassroomSessionModel, r.classroom_session),
					{
						errorCode: '97AF0616',
						errorMessage: DefaultErrorMessage.Saving,
					},
				),
			);
	};

	public archiveSession = (id: number): Observable<ClassroomSessionModel> => {
		return this.httpClient
			.post<{
				classroom_session: unknown;
			}>(`/api/v1/classroom_sessions/${id}/archive`, {})
			.pipe(
				mapResponse(
					(r) => plainToInstance(ClassroomSessionModel, r.classroom_session),
					{
						errorCode: '9700447B',
						errorMessage: DefaultErrorMessage.Saving,
					},
				),
			);
	};

	public deleteSession = (id: number): Observable<unknown> => {
		return this.httpClient
			.delete<{
				classroom_session: unknown;
			}>(`/api/v1/classroom_sessions/${id}`)
			.pipe(
				mapResponse((r) => r, {
					errorCode: 'E780D786',
					errorMessage: DefaultErrorMessage.Saving,
				}),
			);
	};

	public addSessionProgram = (
		session_id: number,
		institute_id: number,
	): Observable<ClassroomSessionModel> => {
		return this.httpClient
			.post<{
				classroom_session: unknown;
			}>(`/api/v1/classroom_sessions/${session_id}/programs`, {
				institute_id,
			})
			.pipe(
				mapResponse((r) => plainToInstance(ClassroomSessionModel, r), {
					errorCode: 'B5882763',
					errorMessage: DefaultErrorMessage.Saving,
				}),
			);
	};

	public removeSessionProgram = (
		session_id: number,
		institute_id: number,
	): Observable<ClassroomSessionModel> => {
		return this.httpClient
			.post<{
				classroom_session: unknown;
			}>(`/api/v1/classroom_sessions/${session_id}/programs/remove`, {
				institute_id,
			})
			.pipe(
				mapResponse((r) => plainToInstance(ClassroomSessionModel, r), {
					errorCode: 'FEBA66EA',
					errorMessage: DefaultErrorMessage.Saving,
				}),
			);
	};
}
